<template>
  <div class="expenses-container">
    <!-- Header -->
    <div class="page-header">
      <h1>Gestion des Dépenses</h1>
      <div class="header-actions">
        <router-link
          to="/expenses/analytics/revenue"
          class="btn btn-analytics revenue"
        >
          <i class="fas fa-chart-line" /> Analytics Revenus
        </router-link>
        <router-link
          to="/expenses/analytics/expenses"
          class="btn btn-analytics expense"
        >
          <i class="fas fa-chart-bar" /> Analytics Dépenses
        </router-link>
        <router-link
          to="/expenses/analytics/supplier-profit"
          class="btn btn-analytics profit"
        >
          <i class="fas fa-coins" /> Profit Fournisseurs
        </router-link>
        <router-link
          to="/expenses/create"
          class="btn btn-primary"
        >
          <i class="fas fa-plus" /> Nouvelle Dépense
        </router-link>
      </div>
    </div>

    <!-- Statistics Cards -->
    <div
      v-if="stats"
      class="stats-grid"
    >
      <div class="stat-card income">
        <div class="stat-icon">
          <i class="fas fa-arrow-down" />
        </div>
        <div class="stat-content">
          <div class="stat-label">
            Revenus
          </div>
          <div class="stat-value">
            {{ formatAmount(stats.total_income) }} DT
          </div>
        </div>
      </div>

      <div class="stat-card expense">
        <div class="stat-icon">
          <i class="fas fa-arrow-up" />
        </div>
        <div class="stat-content">
          <div class="stat-label">
            Dépenses
          </div>
          <div class="stat-value">
            {{ formatAmount(stats.total_expenses) }} DT
          </div>
        </div>
      </div>

      <div
        class="stat-card net"
        :class="{
          'positive': stats.net_revenue > 0,
          'negative': stats.net_revenue < 0
        }"
      >
        <div class="stat-icon">
          <i :class="stats.net_revenue >= 0 ? 'fas fa-wallet' : 'fas fa-exclamation-circle'" />
        </div>
        <div class="stat-content">
          <div class="stat-label">
            Revenu Actuel
          </div>
          <div class="stat-value">
            {{ formatAmount(stats.net_revenue) }} DT
          </div>
        </div>
      </div>
    </div>

    <!-- Filters -->
    <div class="filters-section">
      <div class="filter-group">
        <label>Type</label>
        <select
          v-model="filters.expense_type"
          @change="loadExpenses(); loadStats()"
        >
          <option value="">
            Tous
          </option>
          <option value="OUT">
            Dépenses
          </option>
          <option value="IN">
            Revenus
          </option>
        </select>
      </div>

      <div class="filter-group">
        <label>Statut</label>
        <select
          v-model="filters.status"
          @change="loadExpenses(); loadStats()"
        >
          <option value="">
            Tous
          </option>
          <option value="draft">
            Brouillon
          </option>
          <option value="confirmed">
            Confirmé
          </option>
          <option value="cancelled">
            Annulé
          </option>
        </select>
      </div>

      <div class="filter-group">
        <label>Catégorie</label>
        <select
          v-model="filters.category"
          @change="loadExpenses(); loadStats()"
        >
          <option value="">
            Toutes
          </option>
          <option
            v-for="cat in categories"
            :key="cat.id"
            :value="cat.id"
          >
            {{ cat.name }}
          </option>
        </select>
      </div>

      <div class="filter-group">
        <label>Fournisseur</label>
        <select
          v-model="filters.vendor"
          @change="loadExpenses(); loadStats()"
        >
          <option value="">
            Tous
          </option>
          <option
            v-for="vendor in vendors"
            :key="vendor.id"
            :value="vendor.id"
          >
            {{ vendor.name }}
          </option>
        </select>
      </div>

      <div class="filter-group">
        <label>Livreur</label>
        <select
          v-model="filters.driver"
          @change="loadExpenses(); loadStats()"
        >
          <option value="">
            Tous
          </option>
          <option
            v-for="driver in drivers"
            :key="driver.id"
            :value="driver.id"
          >
            {{ driver.username }}
          </option>
        </select>
      </div>

      <div class="filter-group">
        <label>Date début</label>
        <input
          v-model="filters.start_date"
          type="date"
          @change="loadExpenses(); loadStats()"
        >
      </div>

      <div class="filter-group">
        <label>Date fin</label>
        <input
          v-model="filters.end_date"
          type="date"
          @change="loadExpenses(); loadStats()"
        >
      </div>

      <div class="filter-group search-group">
        <label>Rechercher</label>
        <input
          v-model="filters.search"
          type="text"
          placeholder="Référence, description..."
          @input="debounceSearch"
        >
      </div>

      <div class="filter-group">
        <label>&nbsp;</label>
        <button
          class="btn btn-secondary"
          @click="resetFilters"
        >
          <i class="fas fa-redo" /> Réinitialiser
        </button>
      </div>
    </div>

    <!-- Expenses Table -->
    <div class="table-container">
      <table
        v-if="expenses.length > 0"
        class="expenses-table"
      >
        <thead>
          <tr>
            <th>Référence</th>
            <th>Type</th>
            <th>Date</th>
            <th>Catégorie</th>
            <th>Fournisseur</th>
            <th>Livreur</th>
            <th>Description</th>
            <th>Montant</th>
            <th>Profit</th>
            <th>Statut</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="expense in expenses"
            :key="expense.id"
          >
            <td>
              <router-link
                :to="`/expenses/show/${expense.id}`"
                class="reference-link"
              >
                {{ expense.reference_number }}
              </router-link>
            </td>
            <td>
              <span
                class="type-badge"
                :class="expense.expense_type.toLowerCase()"
              >
                <i :class="expense.expense_type === 'IN' ? 'fas fa-arrow-down' : 'fas fa-arrow-up'" />
                {{ expense.expense_type_display }}
              </span>
            </td>
            <td>{{ formatDate(expense.date) }}</td>
            <td>
              <span
                v-if="expense.category_name"
                class="category-badge"
                :style="{ backgroundColor: expense.category_color }"
              >
                {{ expense.category_name }}
              </span>
              <span
                v-else
                class="text-muted"
              >-</span>
            </td>
            <td>{{ expense.vendor_name || expense.supplier_name || '-' }}</td>
            <td>{{ expense.driver_name || '-' }}</td>
            <td class="description-cell">
              {{ expense.description }}
            </td>
            <td
              class="amount-cell"
              :class="expense.expense_type.toLowerCase()"
            >
              {{ expense.expense_type === 'IN' ? '+' : '-' }}{{ formatAmount(expense.amount) }} DT
            </td>
            <td
              class="profit-cell"
            >
              <span v-if="expense.profit && expense.profit > 0">
                +{{ formatAmount(expense.profit) }} DT
              </span>
              <span
                v-else
                class="text-muted"
              >-</span>
            </td>
            <td>
              <span
                class="status-badge"
                :class="expense.status"
              >
                {{ getStatusLabel(expense.status) }}
              </span>
            </td>
            <td class="actions-cell">
              <router-link
                :to="`/expenses/show/${expense.id}`"
                class="btn-action btn-view"
                title="Voir"
              >
                Voir
              </router-link>
              <router-link
                v-if="expense.status === 'draft'"
                :to="`/expenses/edit/${expense.id}`"
                class="btn-action btn-edit"
                title="Modifier"
              >
                Modifier
              </router-link>
              <button
                v-if="expense.status === 'draft'"
                class="btn-action btn-confirm"
                title="Confirmer"
                @click="confirmExpense(expense)"
              >
                Confirmer
              </button>
              <button
                v-if="expense.status !== 'cancelled'"
                class="btn-action btn-cancel"
                title="Annuler"
                @click="cancelExpense(expense)"
              >
                Annuler
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <div
        v-else-if="loading"
        class="loading-state"
      >
        <i class="fas fa-spinner fa-spin" /> Chargement...
      </div>

      <div
        v-else
        class="empty-state"
      >
        <i class="fas fa-inbox" />
        <p>Aucune dépense trouvée</p>
      </div>
    </div>

    <!-- Pagination -->
    <div
      v-if="pagination.count > pagination.page_size"
      class="pagination"
    >
      <button
        :disabled="!pagination.previous"
        class="btn btn-secondary"
        @click="changePage(pagination.current_page - 1)"
      >
        <i class="fas fa-chevron-left" /> Précédent
      </button>

      <span class="page-info">
        Page {{ pagination.current_page }} sur {{ pagination.total_pages }}
      </span>

      <button
        :disabled="!pagination.next"
        class="btn btn-secondary"
        @click="changePage(pagination.current_page + 1)"
      >
        Suivant <i class="fas fa-chevron-right" />
      </button>
    </div>

    <!-- Confirmation Modal -->
    <div
      v-if="showConfirmModal"
      class="modal-overlay"
      @click.self="closeConfirmModal"
    >
      <div class="confirm-modal">
        <div
          class="modal-icon"
          :class="confirmModalType"
        >
          <i :class="confirmModalType === 'confirm' ? 'fas fa-check-circle' : 'fas fa-times-circle'" />
        </div>
        <h3>{{ confirmModalTitle }}</h3>
        <p>{{ confirmModalMessage }}</p>
        <div class="modal-actions">
          <button
            class="btn btn-secondary"
            @click="closeConfirmModal"
          >
            Annuler
          </button>
          <button
            class="btn"
            :class="confirmModalType === 'confirm' ? 'btn-success' : 'btn-danger'"
            @click="executeConfirmAction"
          >
            {{ confirmModalType === 'confirm' ? 'Confirmer' : 'Annuler la dépense' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'ExpensesIndex',
  data() {
    return {
      expenses: [],
      categories: [],
      vendors: [],
      drivers: [],
      stats: null,
      loading: false,
      filters: {
        expense_type: '',
        status: '',
        category: '',
        vendor: '',
        start_date: '',
        end_date: '',
        search: '',
      },
      pagination: {
        count: 0,
        next: null,
        previous: null,
        current_page: 1,
        total_pages: 1,
        page_size: 20,
      },
      searchTimeout: null,
      // Confirmation modal state
      showConfirmModal: false,
      confirmModalType: 'confirm', // 'confirm' or 'cancel'
      confirmModalTitle: '',
      confirmModalMessage: '',
      confirmModalExpense: null,
    }
  },
  mounted() {
    this.loadExpenses()
    this.loadCategories()
    this.loadVendors()
    this.loadDrivers()
    this.loadStats()
  },
  methods: {
    async loadExpenses(page = 1) {
      this.loading = true
      try {
        const params = {
          page,
          ...this.filters,
        }

        // Remove empty filters
        Object.keys(params).forEach(key => {
          if (params[key] === '' || params[key] === null) {
            delete params[key]
          }
        })

        const response = await axios.get('/api/expenses/', { params })
        this.expenses = response.data.results
        this.pagination = {
          count: response.data.count,
          next: response.data.next,
          previous: response.data.previous,
          current_page: page,
          total_pages: Math.ceil(response.data.count / this.pagination.page_size),
          page_size: this.pagination.page_size,
        }
      } catch (error) {
        console.error('Error loading expenses:', error)
        this.$toast.error('Erreur lors du chargement des dépenses')
      } finally {
        this.loading = false
      }
    },

    async loadCategories() {
      try {
        const response = await axios.get('/api/categories/')
        this.categories = response.data
      } catch (error) {
        console.error('Error loading categories:', error)
      }
    },

    async loadVendors() {
      try {
        const response = await axios.get('/api/vendors/')
        this.vendors = response.data.results || response.data
      } catch (error) {
        console.error('Error loading vendors:', error)
      }
    },

    async loadDrivers() {
      try {
        const response = await axios.get('/api/expenses/drivers/')
        this.drivers = response.data
      } catch (error) {
        console.error('Error loading drivers:', error)
      }
    },

    async loadStats() {
      try {
        const params = { ...this.filters }

        // Remove empty filters
        Object.keys(params).forEach(key => {
          if (params[key] === '' || params[key] === null) {
            delete params[key]
          }
        })

        const response = await axios.get('/api/expenses/stats/', { params })
        this.stats = response.data
      } catch (error) {
        console.error('Error loading stats:', error)
      }
    },

    confirmExpense(expense) {
      // Open confirmation modal
      this.confirmModalType = 'confirm'
      this.confirmModalTitle = 'Confirmer la dépense'
      this.confirmModalMessage = `Êtes-vous sûr de vouloir confirmer la dépense ${expense.reference_number} d'un montant de ${parseFloat(expense.amount).toFixed(3)} DT ?`
      this.confirmModalExpense = expense
      this.showConfirmModal = true
    },

    cancelExpense(expense) {
      // Open cancellation modal
      this.confirmModalType = 'cancel'
      this.confirmModalTitle = 'Annuler la dépense'
      this.confirmModalMessage = `Êtes-vous sûr de vouloir annuler la dépense ${expense.reference_number} ?`
      this.confirmModalExpense = expense
      this.showConfirmModal = true
    },

    closeConfirmModal() {
      this.showConfirmModal = false
      this.confirmModalExpense = null
    },

    async executeConfirmAction() {
      const expense = this.confirmModalExpense
      const actionType = this.confirmModalType
      this.closeConfirmModal()

      if (actionType === 'confirm') {
        try {
          await axios.patch(`/api/expenses/confirm/${expense.id}/`)
          this.$toast.success('Dépense confirmée avec succès')
          this.loadExpenses(this.pagination.current_page)
          this.loadStats()
        } catch (error) {
          console.error('Error confirming expense:', error)
          this.$toast.error('Erreur lors de la confirmation')
        }
      } else if (actionType === 'cancel') {
        try {
          await axios.post(`/api/expenses/cancel/${expense.id}/`)
          this.$toast.success('Dépense annulée avec succès')
          this.loadExpenses(this.pagination.current_page)
          this.loadStats()
        } catch (error) {
          console.error('Error cancelling expense:', error)
          this.$toast.error("Erreur lors de l'annulation")
        }
      }
    },

    debounceSearch() {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.loadExpenses()
      }, 500)
    },

    resetFilters() {
      this.filters = {
        expense_type: '',
        status: '',
        category: '',
        vendor: '',
        driver: '',
        start_date: '',
        end_date: '',
        search: '',
      }
      this.loadExpenses()
      this.loadStats()
    },

    changePage(page) {
      this.loadExpenses(page)
    },

    formatDate(date) {
      return new Date(date).toLocaleDateString('fr-FR')
    },

    formatAmount(amount) {
      return parseFloat(amount).toFixed(3)
    },

    getStatusLabel(status) {
      const labels = {
        draft: 'Brouillon',
        confirmed: 'Confirmé',
        cancelled: 'Annulé',
      }
      return labels[status] || status
    },
  },
}
</script>

<style scoped>
.expenses-container {
  padding: 20px;
  max-width: 100%;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
}

.page-header h1 {
  font-size: 28px;
  font-weight: 600;
  color: #1a202c;
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 12px;
  align-items: center;
}

.btn-analytics {
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;
}

.btn-analytics.revenue {
  background: #d1fae5;
  color: #047857;
}

.btn-analytics.revenue:hover {
  background: #a7f3d0;
  color: #065f46;
}

.btn-analytics.expense {
  background: #fee2e2;
  color: #991b1b;
}

.btn-analytics.expense:hover {
  background: #fecaca;
  color: #7f1d1d;
}

.btn-analytics.profit {
  background: #fef3c7;
  color: #92400e;
}

.btn-analytics.profit:hover {
  background: #fde68a;
  color: #78350f;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.stat-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 15px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: transform 0.2s, box-shadow 0.2s;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.stat-icon {
  width: 50px;
  height: 50px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: white;
}

.stat-card.income .stat-icon {
  background: linear-gradient(135deg, #10b981, #059669);
}

.stat-card.expense .stat-icon {
  background: linear-gradient(135deg, #ef4444, #dc2626);
}

.stat-card.net .stat-icon {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
}

.stat-card.net.positive .stat-icon {
  background: linear-gradient(135deg, #10b981, #059669);
}

.stat-card.net.negative .stat-icon {
  background: linear-gradient(135deg, #ef4444, #dc2626);
}

.stat-card.total .stat-icon {
  background: linear-gradient(135deg, #8b5cf6, #7c3aed);
}

.stat-content {
  flex: 1;
}

.stat-label {
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 5px;
}

.stat-value {
  font-size: 24px;
  font-weight: 700;
  color: #1a202c;
}

.filters-section {
  background: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.filter-group {
  display: flex;
  flex-direction: column;
}

.filter-group label {
  font-size: 13px;
  font-weight: 500;
  color: #4b5563;
  margin-bottom: 5px;
}

.filter-group select,
.filter-group input {
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s;
}

.filter-group select:focus,
.filter-group input:focus {
  outline: none;
  border-color: #3b82f6;
}

.search-group {
  grid-column: span 2;
}

.table-container {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-bottom: 20px;
}

.expenses-table {
  width: 100%;
  border-collapse: collapse;
}

.expenses-table thead {
  background: #f9fafb;
}

.expenses-table th {
  padding: 12px 16px;
  text-align: left;
  font-size: 13px;
  font-weight: 600;
  color: #4b5563;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.expenses-table td {
  padding: 12px 16px;
  border-top: 1px solid #f3f4f6;
  font-size: 14px;
  color: #1f2937;
}

.expenses-table tbody tr:hover {
  background: #f9fafb;
}

.reference-link {
  color: #3b82f6;
  text-decoration: none;
  font-weight: 500;
}

.reference-link:hover {
  text-decoration: underline;
}

.type-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
}

.type-badge.in {
  background: #d1fae5;
  color: #065f46;
}

.type-badge.out {
  background: #fee2e2;
  color: #991b1b;
}

.category-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  color: white;
}

.description-cell {
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.amount-cell {
  font-weight: 600;
  font-family: 'Courier New', monospace;
}

.amount-cell.in {
  color: #059669;
}

.amount-cell.out {
  color: #dc2626;
}

.profit-cell {
  font-weight: 600;
  font-family: 'Courier New', monospace;
  color: #059669;
}

.status-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
}

.status-badge.draft {
  background: #fef3c7;
  color: #92400e;
}

.status-badge.confirmed {
  background: #d1fae5;
  color: #065f46;
}

.status-badge.cancelled {
  background: #f3f4f6;
  color: #6b7280;
}

.actions-cell {
  display: flex;
  gap: 8px;
}

.btn-action {
  padding: 6px 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  text-decoration: none;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
}

.btn-action.btn-view {
  background: #e0e7ff;
  color: #4338ca;
}

.btn-action.btn-view:hover {
  background: #c7d2fe;
  color: #3730a3;
}

.btn-action.btn-edit {
  background: #fef3c7;
  color: #92400e;
}

.btn-action.btn-edit:hover {
  background: #fde68a;
  color: #78350f;
}

.btn-action.btn-confirm {
  background: #d1fae5;
  color: #047857;
}

.btn-action.btn-confirm:hover {
  background: #a7f3d0;
  color: #065f46;
}

.btn-action.btn-cancel {
  background: #fee2e2;
  color: #dc2626;
}

.btn-action.btn-cancel:hover {
  background: #fecaca;
  color: #991b1b;
}

.loading-state,
.empty-state {
  padding: 60px 20px;
  text-align: center;
  color: #6b7280;
}

.empty-state i {
  font-size: 48px;
  margin-bottom: 15px;
  color: #d1d5db;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
}

.page-info {
  font-size: 14px;
  color: #6b7280;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
}

.btn-primary {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: white;
}

.btn-primary:hover {
  background: linear-gradient(135deg, #2563eb, #1d4ed8);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.btn-secondary {
  background: #f3f4f6;
  color: #4b5563;
}

.btn-secondary:hover:not(:disabled) {
  background: #e5e7eb;
}

.btn-secondary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.text-muted {
  color: #9ca3af;
}

/* Confirmation Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
  animation: fadeIn 0.2s ease-out;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.confirm-modal {
  background: white;
  border-radius: 16px;
  padding: 30px;
  max-width: 420px;
  width: 90%;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(-20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.modal-icon {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  font-size: 32px;
}

.modal-icon.confirm {
  background: linear-gradient(135deg, #d1fae5, #a7f3d0);
  color: #059669;
}

.modal-icon.cancel {
  background: linear-gradient(135deg, #fee2e2, #fecaca);
  color: #dc2626;
}

.confirm-modal h3 {
  font-size: 20px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 12px 0;
}

.confirm-modal p {
  font-size: 15px;
  color: #6b7280;
  margin: 0 0 25px 0;
  line-height: 1.5;
}

.modal-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
}

.modal-actions .btn {
  padding: 12px 24px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.modal-actions .btn-secondary {
  background: #f3f4f6;
  color: #4b5563;
}

.modal-actions .btn-secondary:hover {
  background: #e5e7eb;
}

.modal-actions .btn-success {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
}

.modal-actions .btn-success:hover {
  background: linear-gradient(135deg, #059669, #047857);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.modal-actions .btn-danger {
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: white;
}

.modal-actions .btn-danger:hover {
  background: linear-gradient(135deg, #dc2626, #b91c1c);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}
</style>
